// La MANO DEL GIORNO del bridge (tma-orcb-60, condizioni M0-M12): la lettura `bridge.daily.today.read` per `mano` e il `next-board` che la apre. Funzioni PURE, niente rete:
// le due chiamate stanno in `services/limuloApi.ts` (`getBridgeManoDelGiorno`, `playBridgeManoDelGiorno`).
//
// Forma COPIATA dalla misura sul VIVO (tma-orcb, 16/10 ~00:05Z, qbridge-stg 93834aa1aa, qbridge@0.2.0), non inventata: `data.data` porta `sessionId` (`mdg-YYYY-MM-DD`), `day`,
// `status`, `closesAt` (la mezzanotte di Roma, istante ISO), `timeZone` e `me { boardsDone, of, inPlay, played, canPlay, … }`. LETTURA CHIUSA: qualunque altra forma vale `null`.
//
// 🔴 Lo stato lo dice il SERVER (M2): «gia giocata» = `me.played`, «puoi giocare» = `me.canPlay`; MAI derivati qui (niente `boardsDone === of`: conta anche le board abbandonate).
import { bridgeTableIdValido, nuovoClientSeed } from './ticket';

/** `bridge.daily.today.read` e `bridge.daily.next-board` per il formato `mano`. Il `quotidiano` (tma-orcb-61, D1-D3) ha le stesse due capability su un'altra rotta. */
export const BRIDGE_MANO_TODAY_ROUTE = '/v1/games/bridge/daily/mano/today';
export const BRIDGE_MANO_NEXT_BOARD_ROUTE = '/v1/games/bridge/daily/mano/next-board';
export const BRIDGE_QUOTIDIANO_TODAY_ROUTE = '/v1/games/bridge/daily/quotidiano/today';
export const BRIDGE_QUOTIDIANO_NEXT_BOARD_ROUTE = '/v1/games/bridge/daily/quotidiano/next-board';

export interface ManoDelGiorno {
  sessionId: string;
  /** il giorno di ROMA, `YYYY-MM-DD` */
  day: string;
  status: 'open' | 'closed';
  /** la prossima mezzanotte di Roma, istante ISO */
  closesAt: string;
  /** il nome del fuso con cui si legge `day` (`Europe/Rome`): il fuso del dispositivo non decide il giorno */
  timeZone: string;
  played: boolean;
  canPlay: boolean;
  /** il tavolo in gioco adesso, se c'e */
  inPlay: string | null;
}

const asRecord = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const SESSIONE = /^mdg-\d{4}-\d{2}-\d{2}$/;
const GIORNO = /^\d{4}-\d{2}-\d{2}$/;
const ISTANTE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const numero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const fusoValido = (tz: string): boolean => {
  try {
    new Intl.DateTimeFormat('it', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/** La parte comune ai due formati: il parser e CHIUSO PER FORMATO, `sessione` e il solo punto dove i due differiscono (`mdg-` per la mano, `dq-` per il quotidiano). */
function lettura(data: unknown, sessione: RegExp): { base: ManoDelGiorno; me: Record<string, unknown> } | null {
  const r = asRecord(data);
  const me = asRecord(r.me);
  const { sessionId, day, status, closesAt, timeZone } = r;
  if (typeof sessionId !== 'string' || !sessione.test(sessionId)) return null;
  if (typeof day !== 'string' || !GIORNO.test(day)) return null;
  if (status !== 'open' && status !== 'closed') return null;
  if (typeof closesAt !== 'string' || !ISTANTE.test(closesAt) || !Number.isFinite(Date.parse(closesAt))) return null;
  if (typeof timeZone !== 'string' || !fusoValido(timeZone)) return null;
  if (typeof me.played !== 'boolean' || typeof me.canPlay !== 'boolean') return null;
  if (!numero(me.boardsDone) || !numero(me.of)) return null;
  if (me.inPlay !== null && typeof me.inPlay !== 'string') return null;
  return { base: { sessionId, day, status, closesAt, timeZone, played: me.played, canPlay: me.canPlay, inPlay: me.inPlay }, me };
}

/** Il `data` del game host (gia fuori dalla busta `{ provider, data }`) -> la mano, o `null` se la forma non e quella misurata. */
export const bridgeManoOf = (data: unknown): ManoDelGiorno | null => lettura(data, SESSIONE)?.base ?? null;

/** Il DUPLICATO QUOTIDIANO (tma-orcb-61, D1): la stessa lettura piu i contatori della sessione (`boards` x `instances`) e di chi gioca (`me`). Servono SOLO al testo («board k di n»). */
export interface QuotidianoDelGiorno extends ManoDelGiorno {
  boards: number;
  instances: number;
  boardsDone: number;
  boardsCompleted: number;
  boardsAbandoned: number;
  of: number;
}
const SESSIONE_QUOTIDIANO = /^dq-\d{4}-\d{2}-\d{2}$/;
const intero = (v: unknown, min: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min;

/** Come `bridgeManoOf`, ma per `dq-` e `kind: "daily"`: un payload della mano vale `null` qui, e viceversa. */
export function bridgeQuotidianoOf(data: unknown): QuotidianoDelGiorno | null {
  const r = asRecord(data);
  const l = lettura(data, SESSIONE_QUOTIDIANO);
  if (l === null || r.kind !== 'daily') return null;
  const { boards, instances } = r;
  const { enrolled, boardsDone, boardsCompleted, boardsAbandoned, of } = l.me;
  if (!intero(boards, 1) || !intero(instances, 1) || typeof enrolled !== 'boolean') return null;
  if (!intero(boardsDone, 0) || !intero(boardsCompleted, 0) || !intero(boardsAbandoned, 0) || !intero(of, 0)) return null;
  return { ...l.base, boards, instances, boardsDone, boardsCompleted, boardsAbandoned, of };
}

/**
 * Gli stati del quotidiano (D2), TUTTI dal server: `canPlay` (inizia/continua/riprendi) o no (completato). `boardsDone` e `of` danno solo i numeri del testo, MAI `played` o `canPlay`.
 * Senza `canPlay`: `played` o nessuna abbandonata = «completato»; una board abbandonata che non si rigioca = «completato, N abbandonate» (il N viene dal server).
 */
export type StatoQuotidiano = 'inizia' | 'riprendi' | 'continua' | 'completato' | 'completatoAbbandonate';
export const statoQuotidiano = (q: QuotidianoDelGiorno): StatoQuotidiano =>
  q.canPlay
    ? q.inPlay !== null
      ? 'riprendi'
      : q.boardsDone === 0
        ? 'inizia'
        : 'continua'
    : q.played || q.boardsAbandoned === 0
      ? 'completato'
      : 'completatoAbbandonate';

/** Da dove si e arrivati al tavolo (D4): decide SOLO il bottone di fine mano, mai un permesso. Sta nel parametro `da` della rotta (sopravvive al ricaricamento), contro un elenco CHIUSO. */
export const CONTESTI_TAVOLO = ['quotidiano', 'mano'] as const;
export type ContestoTavolo = (typeof CONTESTI_TAVOLO)[number];
export const contestoTavoloOf = (v: string | null): ContestoTavolo | null => CONTESTI_TAVOLO.find((c) => c === v) ?? null;
export const tavoloDa = (tableId: string, da: ContestoTavolo): string => `/bridge/tavolo/${tableId}?da=${da}`;

/** I TRE stati della card (M2 + aggiunta di qbridge-orcb): `riprendi` e `gioca` hanno `canPlay`; `giocata` ha `played`; `chiusa` = ne' l'uno ne' l'altro (es. una board abbandonata). */
export type StatoMano = 'gioca' | 'riprendi' | 'giocata' | 'chiusa';
export const statoMano = (m: ManoDelGiorno): StatoMano => (m.canPlay ? (m.inPlay !== null ? 'riprendi' : 'gioca') : m.played ? 'giocata' : 'chiusa');

/** Il `next-board` risponde `{ tableId, position, of, resumed, wsUrl }`: serve il tavolo. Il `wsUrl` (anche `""`) si ignora: l'indirizzo del socket e quello del ticket. */
export function bridgeNextBoardOf(data: unknown): { tableId: string } | null {
  const tableId = asRecord(data).tableId;
  return typeof tableId === 'string' && bridgeTableIdValido(tableId) ? { tableId: tableId.toLowerCase() } : null;
}

/** La chiave di idempotenza di UN tocco (nel corpo, `[A-Za-z0-9._:@-]{8,200}`). Senza `crypto` non c'e (come il `clientSeed`): `null` e la mano non si apre. */
export const nuovaChiaveMano = (): string | null => nuovaChiave('mdg');
/** Quella del quotidiano: prefisso diverso, stessa regola (una per TOCCO). */
export const nuovaChiaveQuotidiano = (): string | null => nuovaChiave('dq');
function nuovaChiave(prefisso: string): string | null {
  const seme = nuovoClientSeed();
  return seme === null ? null : `${prefisso}-${seme}`;
}

/** «Chiude fra» in ore e minuti (i minuti per eccesso: negli ultimi 59 s resta 1 min); `null` a `closesAt` passato -> si rilegge `today`, perche il giorno cambia. */
export function chiudeFra(closesAt: string, ora: number): { ore: number; minuti: number } | null {
  const ms = Date.parse(closesAt) - ora;
  if (!(ms > 0)) return null;
  const minuti = Math.ceil(ms / 60_000);
  return { ore: Math.floor(minuti / 60), minuti: minuti % 60 };
}

/** Il giorno come si scrive a schermo: `day` letto nel fuso del PAYLOAD (mezzogiorno UTC di quel giorno, mai l'ora locale del dispositivo). */
export function giornoMostrato(day: string, timeZone: string, lingua: string): string {
  const [a, m, g] = day.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(lingua, { day: 'numeric', month: 'long', timeZone }).format(new Date(Date.UTC(a, m - 1, g, 12)));
}
